import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { simulationFields } from 'queries'
import { IInformationBody, IEmployee, IFactor, IQueryParams, ISimulationConfig } from 'interfaces'
import { get, orderBy, sumBy } from 'lodash'
import { calcSimulate, verifyLimits, isModality as CheckModality, formatErrors } from 'helpers'
import moment from 'moment'

import useApi from './useApi'
import useCore from './useCore'
import IInsurer from 'interfaces/insurer'
import useLayout from './useLayout'

interface ICloneState {
  optar_por_seguro?: boolean
  data_primeiro_vencimento?: string
  valor_parcela?: number
  valor_solicitado?: number
  valor_taxa_mes?: number
  isRefresh?: boolean
  id_taxa_grupo_corban?: string
}

interface IHooksSimulation {
  queryParams?: IQueryParams
  pathSimulation: string
  isRenegotiation: boolean
  isParcel: boolean
  pathEmployeeInformation?: string
  informationsForm?: IInformationBody
  enabledSecurity?: boolean
  isConsignedCard?: boolean
  isTaxRange?: boolean
}

export interface IFirstFactor {
  parcel: {
    min: number
    max: number
  }
  value: {
    min: number
    max: number
  }
}

interface GetMinMaxParams {
  factor?: IFactor
  parcel?: boolean
}

export function useSimulationConfig({
  pathSimulation,
  queryParams,
  isRenegotiation,
  isParcel = false,
  pathEmployeeInformation,
  informationsForm,
  enabledSecurity,
  isConsignedCard = false,
  isTaxRange = false,
}: IHooksSimulation) {
  const { showSnackBar } = useLayout()
  const { apiRequest, SUBMODULE_PATHS } = useApi()
  const { entity } = useCore()

  // Objeto de configuração de dados necessários para simulação
  const [simulationConfig, setSimulationConfig] = useState<ISimulationConfig>()
  // Campos principais para cálculo
  const [selectedFactor, setSelectedFactor] = useState<IFactor>()
  const [valueRequest, setValueRequest] = useState<number>(0)
  const [totalValue, setTotalValue] = useState<number>(0)
  const [selectedFirstFactor, setSelectedFirstFactor] = useState<number>(0)
  const [valueRenegociateContracts, setValueRenegociateContracts] = useState<number>(0)
  // Habilita seguro
  const [showContractInsurer, setShowContractInsurer] = useState<boolean>(false)
  // Valores referente ao cálculo com seguro
  const [valueInsurer, setValueInsurer] = useState<number>(0)
  const [insurer, setInsurer] = useState<IInsurer>()
  const [isRefreshInformations, setIsRefreshInformations] = useState<boolean>(false)
  const [disableButtonState, setDisableButtonState] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)
  const [employee, setEmployee] = useState<IEmployee>()

  const isCallPost = useRef<boolean>(false)
  const stateParamsRef = useRef<ICloneState>({} as ICloneState)
  const queryParamsRef = useRef<IQueryParams>(queryParams || {})
  const [formStateValues, setFormStateValues] = useState<ICloneState>({} as ICloneState)
  const [isDate, setIsDate] = useState<string>(moment().add(30, 'days').format('YYYY-MM-DD'))
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const isModality = CheckModality(entity)
  const isCDC = isModality === 'CDC'
  const isCorban = isModality === 'CORBAN'
  const isCP = isModality === 'CP'
  const isCEP = isModality === 'CEP'

  const isSalesChannel = isCP || isCDC
  const isCPandnotRenegociate = isCP && !isRenegotiation

  const allowTaxChange = useMemo(() => {
    return (
      (['CP', 'CDC'].includes(isModality) && !isRenegotiation) ||
      (isModality === 'CORBAN' && (isConsignedCard || isTaxRange))
    )
  }, [isConsignedCard, isModality, isRenegotiation, isTaxRange])

  const isMinTaxNull = useMemo(() => {
    return selectedFactor?.taxa_minima === null
  }, [selectedFactor])

  const isCarenciaNull = useMemo(() => {
    return selectedFactor?.carencia === null || selectedFactor?.carencia === undefined
  }, [selectedFactor])

  const anyZeroFactor = useMemo(() => {
    return (
      (selectedFactor?.parcela_minima === 0 && selectedFactor?.parcela_maxima === 0) ||
      (selectedFactor?.valor_maximo === 0 && selectedFactor?.valor_minimo === 0)
    )
  }, [selectedFactor])

  const isEveryTaxesZero = useMemo(() => {
    return simulationConfig?.fatores.every(
      (fact) => fact.valor_maximo === 0 && fact.valor_minimo === 0
    )
  }, [simulationConfig])

  const handleChange = (value: number) => {
    setValueRequest(value)
  }

  const handleChangeParcela = (value: IFactor) => {
    setSelectedFactor(value)
  }

  const handleBlur = (value: number) => {
    setValueRequest(value)
    setFormStateValues((prevState) => ({
      ...prevState,
      ...(isParcel ? { valor_parcela: value } : { valor_solicitado: value }),
    }))
    setIsRefreshInformations(false)
  }

  const handleFocus = () => {
    setIsRefreshInformations(true)
  }

  const getFactorBasedOnSelected = useCallback(
    (orderedFactors: IFactor[], reset?: boolean) => {
      const maximumFactor = get(
        orderedFactors,
        `[${orderedFactors ? orderedFactors?.length - 1 : 0}]`
      )
      const alreadySelectedFactor =
        orderedFactors.find((item) => item.prazo === selectedFactor?.prazo) || maximumFactor
      return reset ? maximumFactor : alreadySelectedFactor
    },
    [selectedFactor]
  )

  // Pegar limite minimo
  const getMinValue = ({ factor, parcel }: GetMinMaxParams) => {
    const currentMode = parcel !== undefined ? parcel : isParcel
    if (selectedFactor) {
      const currentFactor = factor || selectedFactor

      if (simulationConfig && simulationConfig.limites) {
        if (currentMode) {
          if (currentFactor.parcela_minima >= simulationConfig.limites.financeira_valor_minimo) {
            return Math.round(currentFactor.parcela_minima * 100) / 100
          }
          if (currentFactor.parcela_minima > 0) {
            return (
              simulationConfig.limites.financeira_valor_minimo /
              (allowTaxChange ? 1 : currentFactor.fator)
            )
          }
          return 0
        }

        if (currentFactor.valor_minimo >= simulationConfig.limites.financeira_valor_minimo) {
          return currentFactor.valor_minimo
        }
        if (currentFactor.valor_minimo > 0) {
          return simulationConfig.limites.financeira_valor_minimo
        }
      }
      return currentFactor.valor_minimo
    }
    return 0
  }

  // Função para saber o valor máximo da financeira
  const isFinancialMaxValue = (factor?: IFactor) => {
    if (selectedFactor) {
      const currentFactor = factor || selectedFactor
      if (simulationConfig) {
        const maxValueSimulation = simulationConfig.limites?.financiamento
          ? simulationConfig.limites.financiamento
          : simulationConfig.limites.financeira_valor_maximo
        return maxValueSimulation < currentFactor.valor_maximo
      }
    }
    return false
  }

  // Limite máximo
  const getMaxValues = ({ factor, parcel }: GetMinMaxParams) => {
    const currentMode = parcel !== undefined ? parcel : isParcel

    if (selectedFactor) {
      const currentFactor = factor || selectedFactor
      const maxValue = currentMode ? currentFactor.parcela_maxima : currentFactor.valor_maximo

      if (isFinancialMaxValue(factor) && simulationConfig) {
        const maxValueSimulation = simulationConfig.limites?.financiamento
          ? simulationConfig.limites.financiamento
          : simulationConfig.limites.financeira_valor_maximo
        return currentMode
          ? maxValueSimulation / (isCPandnotRenegociate ? 1 : currentFactor.fator)
          : maxValueSimulation
      }
      return isParcel ? Math.floor(maxValue * 100) / 100 : maxValue
    }
    return 0
  }

  const initialValues = () => {
    if (selectedFactor) {
      if (isParcel) {
        return isRenegotiation ? valueRenegociateContracts / selectedFactor.fator : getMaxValues({})
      }
      return isRenegotiation ? valueRenegociateContracts : getMaxValues({})
    }
    return 0
  }

  const resetFields = async () => {
    const maxValue = initialValues()
    if (simulationConfig && !allowTaxChange) {
      const lastElement = simulationConfig.fatores[simulationConfig.fatores.length - 1]

      setValueRequest(maxValue)
      setSelectedFactor(lastElement)
    }
    if (allowTaxChange && selectedFactor) {
      const nextMonthDate = moment().add(30, 'days').format('YYYY-MM-DD')
      setFormStateValues((prevState) => ({
        ...prevState,
        valor_taxa_mes: undefined,
        data_primeiro_vencimento: nextMonthDate,
      }))
      setIsDate(nextMonthDate)

      setValueRequest(maxValue)
      await getSimulationConfig(undefined)
    }
  }

  // Bloqueio do input para solicitação e renegociação de crédito
  const isBlockedInput = () => {
    if (
      isRefreshInformations ||
      getMinValue({}) > getMaxValues({}) ||
      anyZeroFactor ||
      isEveryTaxesZero ||
      (isSalesChannel && !disableButtonState && !isRenegotiation)
    ) {
      return true
    }

    return false
  }

  const verifyFutureDate = (date: string) => {
    const dataFutura = moment(date)

    // Verificar se é fim de semana
    const isFimDeSemana = dataFutura.day() === 0 || dataFutura.day() === 6

    return {
      isFimDeSemana,
    }
  }

  const getSimulationConfig = useCallback(
    async (valor_taxa_mes = undefined) => {
      setIsLoading(true)
      const response = await apiRequest({
        path: pathSimulation,
        method: 'GET',
        queryParams: {
          ...queryParamsRef.current,
          fields: simulationFields,
          valor_taxa_mes,
          id_taxa_grupo_corban:
            formStateValues.id_taxa_grupo_corban || queryParamsRef.current.id_taxa_grupo_corban,
        },
      })

      if (response && !response?.data) {
        const orderedFactors: IFactor[] = orderBy(response.fatores, 'prazo')
        const contractValues = response.contratos ? sumBy(response.contratos, 'saldo_devedor') : 0
        const isContainsInsurer = response?.seguradora
        const factor = getFactorBasedOnSelected(orderedFactors)

        if (allowTaxChange && !isCorban) {
          setSelectedFactor(factor)
          isCallPost.current = true
          return
        }

        if (allowTaxChange && valor_taxa_mes) {
          stateParamsRef.current.valor_taxa_mes = valor_taxa_mes
        }

        if (!valor_taxa_mes && isTaxRange && factor) {
          stateParamsRef.current.valor_taxa_mes = factor.taxa_nominal
        }

        if (isContainsInsurer) {
          setShowContractInsurer(true)
          setInsurer(isContainsInsurer)
        }

        if (contractValues) setValueRenegociateContracts(contractValues)
        setSimulationConfig({
          ...response,
          fatores: orderedFactors,
          seguradora: isContainsInsurer,
        })
        if (factor) {
          setSelectedFirstFactor(factor?.prazo)
          setSelectedFactor(factor)
        } else {
          setSelectedFactor({} as IFactor)
        }
        setIsLoading(false)
        return
      } else {
        setError((prevState) => !prevState)
        setIsLoading(false)
      }
    },
    [
      apiRequest,
      isCorban,
      allowTaxChange,
      isTaxRange,
      pathSimulation,
      formStateValues.id_taxa_grupo_corban,
      getFactorBasedOnSelected,
    ]
  )

  const postSimulationConfig = useCallback(
    async (value?: number, reset?: boolean) => {
      isCallPost.current = false
      const taxValue = reset ? undefined : formStateValues.valor_taxa_mes
      if (isCorban) {
        return await getSimulationConfig(taxValue)
      }
      setIsLoading(true)
      const { isFimDeSemana } = verifyFutureDate(isDate)
      const responsePost = await apiRequest({
        path: SUBMODULE_PATHS.SIMULATE_CREDIT_POST,
        method: 'POST',
        body: {
          optar_por_seguro: enabledSecurity,
          cliente: informationsForm?.cpf,
          ...(isMinTaxNull ? {} : { valor_taxa_mes: taxValue }),
          ...(!isFimDeSemana || isCarenciaNull ? { data_primeiro_vencimento: isDate } : {}),
          ...(isParcel
            ? { valor_parcela: value ? value : valueRequest }
            : { valor_solicitado: value ? value : valueRequest }),
        },
        disableErrors: true,
      })

      if (responsePost && !responsePost.data) {
        stateParamsRef.current = {
          optar_por_seguro: enabledSecurity!,
          ...(isCarenciaNull ? { data_primeiro_vencimento: isDate } : {}),
          isRefresh: false,
          ...(isParcel
            ? { valor_parcela: value ?? valueRequest }
            : { valor_solicitado: value ?? valueRequest }),
          ...(isMinTaxNull ? {} : { valor_taxa_mes: taxValue }),
        }
        setFormStateValues((prevState) => ({
          optar_por_seguro: enabledSecurity!,
          ...(isCarenciaNull ? { data_primeiro_vencimento: isDate } : {}),
          isRefresh: false,
          ...(isParcel
            ? { valor_parcela: value ?? valueRequest }
            : { valor_solicitado: value ?? valueRequest }),
          ...(isMinTaxNull ? {} : { valor_taxa_mes: prevState.valor_taxa_mes }),
        }))
        const orderedFactors = orderBy(responsePost.fatores, 'prazo') as IFactor[]
        const factor = getFactorBasedOnSelected(orderedFactors, reset)
        const isContainsInsurer = responsePost?.seguradora

        if (isContainsInsurer) {
          setShowContractInsurer(true)
          setInsurer(isContainsInsurer)
        }

        setSimulationConfig({
          ...responsePost,
          fatores: orderedFactors,
          seguradora: isContainsInsurer,
        })

        if (factor) {
          setTotalValue(isParcel ? factor.valor_liberado : factor.recebivel)
          setSelectedFirstFactor(factor?.prazo)
          setSelectedFactor(factor)
        } else {
          setSelectedFactor({} as IFactor)
        }
      } else {
        showSnackBar({
          type: 'error',
          message: formatErrors(responsePost.data),
        })
      }
      setIsLoading(false)
    },
    [
      isDate,
      apiRequest,
      enabledSecurity,
      informationsForm,
      isMinTaxNull,
      isCarenciaNull,
      formStateValues.valor_taxa_mes,
      isParcel,
      valueRequest,
      showSnackBar,
      SUBMODULE_PATHS,
      isCorban,
      getSimulationConfig,
      getFactorBasedOnSelected,
    ]
  )

  const getEmployees = useCallback(async () => {
    if (pathEmployeeInformation) {
      const response = await apiRequest({
        path: pathEmployeeInformation,
        method: 'GET',
        queryParams: {
          fields: 'nome,sobrenome,id,cpf,telefone_celular',
          search: queryParams?.funcionario_cpf?.replace(/[^\d]+/g, ''),
        },
      })

      if (response && response.results?.length) {
        setEmployee(response.results[0])
        return
      }
      setEmployee(undefined)
      return
    }
  }, [apiRequest, queryParams, pathEmployeeInformation])

  useEffect(() => {
    getSimulationConfig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formStateValues.id_taxa_grupo_corban])

  useEffect(() => {
    if (isCorban) getEmployees()
  }, [getEmployees, isCorban])

  useEffect(() => {
    if (selectedFactor) {
      if (valueRequest === 0) {
        handleChange(initialValues())
      } else {
        const maxValue = getMaxValues({})
        const minValue = getMinValue({})
        handleChange(verifyLimits(valueRequest, maxValue, minValue))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFactor])

  useEffect(() => {
    handleChange(initialValues())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isParcel])

  useEffect(() => {
    if (selectedFactor) {
      const maxValue = getMaxValues({ parcel: false })
      const minValue = getMinValue({ parcel: false })
      const maxValueParcel = getMaxValues({ parcel: true })
      const minValueParcel = getMinValue({ parcel: true })

      const { newValue, newInsurerValue } = calcSimulate({
        values: valueRequest,
        factor: selectedFactor,
        allowTaxChange,
        maxValue,
        minValue,
        maxValueParcel,
        minValueParcel,
        isParcel: isParcel,
        insurer,
        isRenegotiation,
        enabledSecurity,
        isCEP,
        isCorban,
      })

      setTotalValue(newValue)
      setValueInsurer(newInsurerValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueRequest, selectedFactor, isParcel, insurer, allowTaxChange, enabledSecurity])

  useEffect(() => {
    if (isCallPost.current && selectedFactor) {
      postSimulationConfig(selectedFactor.valor_maximo)
    }
  }, [selectedFactor, isCallPost, postSimulationConfig])

  return {
    simulationConfig,
    selectedFactor,
    selectedFirstFactor,
    valueRenegociateContracts,
    valueInsurer,
    error,
    queryParamsRef,
    stateParamsRef,
    valueRequest,
    totalValue,
    isRefreshInformations,
    showContractInsurer,
    employee,
    isDate,
    formStateValues,
    disableButtonState,
    isLoading,
    allowTaxChange,
    isMinTaxNull,
    isCarenciaNull,
    anyZeroFactor,
    isCorban,
    getMinValue,
    getMaxValues,
    initialValues,
    handleChange,
    handleChangeParcela,
    resetFields,
    isBlockedInput,
    setSelectedFactor,
    handleBlur,
    handleFocus,
    postSimulationConfig,
    setIsRefreshInformations,
    setIsDate,
    setFormStateValues,
    setDisableButtonState,
    setIsLoading,
    isCallPost,
    isEveryTaxesZero,
  }
}

export default useSimulationConfig
